import React, { useState } from 'react';
import { Mail, Lock, LogIn, Chrome, AlertCircle } from 'lucide-react';
import { auth } from '../firebase';
import {
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
} from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../context/ToastContext';
import { useAppContext } from '../context/AppContext';

const AdminAccess = () => {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { user, isAdmin } = useAppContext();

  const [loginMethod, setLoginMethod] = useState('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(''); // ✅ NEW: State for error message

  // ✅ Email/Password Login
  const handleEmailPasswordLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage(''); // Clear previous errors

    try {
      if (!email || !password) {
        setErrorMessage('Please enter email and password');
        addToast('⚠️ Please enter email and password', 'error');
        setIsLoading(false);
        return;
      }

      console.log('🔐 Signing in with email/password:', email);
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const authenticatedUser = userCredential.user;

      console.log('✅ Email/Password login successful:', authenticatedUser.email);

      // Get custom claims
      const idTokenResult = await authenticatedUser.getIdTokenResult();
      const isAdminUser = idTokenResult.claims.admin === true || idTokenResult.claims.super_admin === true;

      addToast(`✅ Welcome, ${authenticatedUser.email}!`, 'success');

      setTimeout(() => {
        if (isAdminUser) {
          navigate('/admin');
        } else {
          setErrorMessage('You do not have admin access');
          addToast('⚠️ You do not have admin access', 'error');
        }
      }, 500);
    } catch (error) {
      console.error('❌ Email/Password login error:', error);
      console.error('❌ Error code:', error.code);
      console.error('❌ Error message:', error.message);

      // ✅ IMPROVED: Handle all common Firebase auth errors
      let errorMsg = 'Login failed. Please try again.';
      
      switch (error.code) {
        case 'auth/user-not-found':
        case 'auth/wrong-password':
        case 'auth/invalid-credential':
        case 'auth/invalid-login-credentials':
          errorMsg = 'Invalid email or password.';
          break;
        case 'auth/invalid-email':
          errorMsg = 'Please enter a valid email address.';
          break;
        case 'auth/user-disabled':
          errorMsg = 'This account has been disabled.';
          break;
        case 'auth/too-many-requests':
          errorMsg = 'Too many failed attempts. Please try again later.';
          break;
        case 'auth/network-request-failed':
          errorMsg = 'Network error. Please check your connection.';
          break;
        default:
          errorMsg = `Login failed: ${error.message}`;
      }

      setErrorMessage(errorMsg); // ✅ Set error in UI
      addToast(`❌ ${errorMsg}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // ✅ Google Sign-In
  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    setErrorMessage(''); // Clear previous errors

    try {
      console.log('🔐 Signing in with Google...');
      const provider = new GoogleAuthProvider();
      const userCredential = await signInWithPopup(auth, provider);
      const authenticatedUser = userCredential.user;

      console.log('✅ Google login successful:', authenticatedUser.email);

      const idTokenResult = await authenticatedUser.getIdTokenResult();
      const isAdminUser = idTokenResult.claims.admin === true || idTokenResult.claims.super_admin === true;

      addToast(`✅ Welcome, ${authenticatedUser.displayName || authenticatedUser.email}!`, 'success');

      setTimeout(() => {
        if (isAdminUser) {
          navigate('/admin');
        } else {
          setErrorMessage('You do not have admin access');
          addToast('⚠️ You do not have admin access', 'error');
        }
      }, 500);
    } catch (error) {
      console.error('❌ Google login error:', error);
      console.error('❌ Error code:', error.code);

      let errorMsg = 'Google login failed. Please try again.';

      switch (error.code) {
        case 'auth/popup-closed-by-user':
          errorMsg = 'Sign-in cancelled.';
          addToast(`⚠️ ${errorMsg}`, 'warning');
          setIsLoading(false);
          return;
        case 'auth/popup-blocked':
          errorMsg = 'Pop-up was blocked by your browser. Please allow pop-ups for this site.';
          break;
        case 'auth/account-exists-with-different-credential':
          errorMsg = 'An account already exists with this email using a different sign-in method.';
          break;
        case 'auth/network-request-failed':
          errorMsg = 'Network error. Please check your connection.';
          break;
        default:
          errorMsg = error.message;
      }

      setErrorMessage(errorMsg); // ✅ Set error in UI
      addToast(`❌ ${errorMsg}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">🔐 Admin Access</h1>
          <p className="text-slate-400">Please log in to access the administrator dashboard.</p>
        </div>

        <div className="bg-white rounded-lg shadow-2xl p-8">
          {/* ✅ ERROR MESSAGE BANNER */}
          {errorMessage && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-red-800 font-semibold">Login Failed</p>
                <p className="text-red-700 text-sm">{errorMessage}</p>
              </div>
            </div>
          )}

          <div className="flex gap-2 mb-8">
            <button
              onClick={() => {
                setLoginMethod('email');
                setErrorMessage(''); // Clear error when switching tabs
              }}
              className={`flex-1 py-2 px-4 rounded-lg font-semibold transition ${
                loginMethod === 'email'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              📧 Email & Password
            </button>
            <button
              onClick={() => {
                setLoginMethod('google');
                setErrorMessage(''); // Clear error when switching tabs
              }}
              className={`flex-1 py-2 px-4 rounded-lg font-semibold transition ${
                loginMethod === 'google'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              🔵 Google
            </button>
          </div>

          {loginMethod === 'email' && (
            <form onSubmit={handleEmailPasswordLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-slate-900 mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setErrorMessage(''); // Clear error when user types
                    }}
                    placeholder="admin@odilogistics.com"
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition"
                    disabled={isLoading}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-900 mb-2">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setErrorMessage(''); // Clear error when user types
                    }}
                    placeholder="••••••••"
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition"
                    disabled={isLoading}
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-bold py-2 px-4 rounded-lg transition flex items-center justify-center gap-2 cursor-pointer"
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Signing in...
                  </>
                ) : (
                  <>
                    <LogIn className="h-5 w-5" /> Sign In
                  </>
                )}
              </button>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-4 text-sm">
                <p className="font-semibold text-blue-900 mb-2">📝 Only for Administrators :</p>
                <p className="text-blue-800">Email: <code className="bg-blue-100 px-2 py-1 rounded">yourName@odi-logistics.com</code></p>
                <p className="text-blue-800">Password: <code className="bg-blue-100 px-2 py-1 rounded">23Odi*£$32</code></p>
              </div>
            </form>
          )}

          {loginMethod === 'google' && (
            <div className="space-y-4">
              <p className="text-center text-gray-600 mb-6">Click the button below to sign in with your Google account.</p>
              <button
                onClick={handleGoogleSignIn}
                disabled={isLoading}
                className="w-full bg-white border-2 border-gray-300 hover:border-blue-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed text-gray-700 font-bold py-3 px-4 rounded-lg transition flex items-center justify-center gap-3 cursor-pointer"
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                    Signing in with Google...
                  </>
                ) : (
                  <>
                    <Chrome className="h-6 w-6 text-blue-600" /> Sign In with Google
                  </>
                )}
              </button>

              <p className="text-center text-xs text-gray-500 mt-4">
                We'll never post to Google without your permission.
              </p>
            </div>
          )}
        </div>

        <div className="text-center mt-6 text-slate-400 text-sm">
          <p>🔒 Secure login powered by Firebase Authentication</p>
        </div>
      </div>
    </div>
  );
};

export default AdminAccess;